import { ChangeEvent, useState } from 'react';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';

import { useSolana } from '../hooks/useSolana';
import { removeKeypairDuplicates } from '../utils/remove-keypair-duplicates';
import parseCsv from '../utils/parse-csv';
import { csvDownloader } from '../utils/csv-downloader';
import { customSolDisperceExample, customSolCollectorExample } from '../utils/csv-examples';
import bs58 from 'bs58';
import { WSOL } from '@raydium-io/raydium-sdk';
import { useSplToken } from '../hooks/useSplToken';
import '../SolManagerCsv.css';
import { solManagerJitoTip } from '../../ConfigFees';
import { solManagerDisperseForBundleBuyFeesExtra, solManagerDisperseForMixWalletFees, solManagerVolumeBuyExtra } from '../../ConfigConstants';
import getRandomizedArray from '../utils/get-randomized-array';

export function SolManagerCsv({
    connection,
    allWallets,
    deployer,
    walletLoaded,
    allWalletRoles,
    mixWallets,
    bundleBuyWallets,
    bundleBuyValues,
    funder,
    volFunder,
    holder,
    freezer,
    volumeBuyWallets,
    volumeBuyAmounts,
    logMessage,
}: {
    connection: Connection;
    allWallets: Keypair[];
    deployer: Keypair;
    walletLoaded: boolean;
    allWalletRoles: string[];
    mixWallets: PublicKey[];
    bundleBuyWallets: PublicKey[];
    bundleBuyValues: number[];
    funder: Keypair;
    volFunder: Keypair | undefined;
    holder: Keypair;
    freezer: Keypair;
    volumeBuyWallets: Keypair[];
    volumeBuyAmounts: number[];
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const { getMultipleSolBalances, multipleSolanaTrasfersWithJito } = useSolana();
    const { transaferSolana } = useSolana();
    const { burnAllTokenAccountsWithJito } = useSplToken();
    const { wrapSol } = useSplToken();
    const [sourceWalletOfSol, setSourceWalletOfSol] = useState(0);
    const sourceWalletOfSolChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSourceWalletOfSol(parseInt(event.target.value, 10));
    };

    const [customSourceWalletOfSol, setCustomSourceWalletOfSol] = useState(0);
    const customSourceWalletOfSolChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCustomSourceWalletOfSol(parseInt(event.target.value, 10));
    };

    const [walletToReceiveAllSol, setWalletToReceiveAllSol] = useState(0);
    const walletToReceiveAllSolChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setWalletToReceiveAllSol(parseInt(event.target.value, 10));
    };

    const [customWalletToReceiveAllSol, setCustomWalletToReceiveAllSol] = useState(0);
    const customWalletToReceiveAllSolChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCustomWalletToReceiveAllSol(parseInt(event.target.value, 10));
    };

    const [disperseSolCsvFile, setDisperseSolCsvFile] = useState<File | null>(null);

    const [splitterPublikKey, setSplitterPublikKey] = useState<string>('');
    const [splitterJitoTip, setSplitterJitoTip] = useState<number>(solManagerJitoTip / LAMPORTS_PER_SOL);
    const [splitterSolAmount, setSplitterSolAmount] = useState<number>(0);

    const [useJitoManualTransfer, setUseJitoManualTransfer] = useState(false);

    const splitterPublikKeyChange = (event: { target: { value: string } }) => {
        setSplitterPublikKey(event.target.value);
    };
    const splitterSolAmountChange = (event: { target: { value: string } }) => {
        setSplitterSolAmount(Number(event.target.value));
    };

    const disperseSolCsv = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && file.type === 'text/csv') {
            setDisperseSolCsvFile(file);

            const fileNameLabel = document.querySelector('.disperse-sol-csv-file-name');
            if (fileNameLabel) {
                fileNameLabel.textContent = file.name;
            }
        } else {
            setDisperseSolCsvFile(null);
        }
    };

    const [collectSolCsvFile, setCollectSolCsvFile] = useState<File | null>(null);
    const collectSolCsv = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && file.type === 'text/csv') {
            setCollectSolCsvFile(file);

            const fileNameLabel = document.querySelector('.collect-sol-csv-file-name');
            if (fileNameLabel) {
                fileNameLabel.textContent = file.name;
            }
        } else {
            setCollectSolCsvFile(null);
        }
    };

    const downloadCustomSolDisperceExample = async () => csvDownloader(customSolDisperceExample, 'customSolDisperceExample');
    const downloadCustomSolCollectorExample = async () => csvDownloader(customSolCollectorExample, 'customSolCollectorExample');

    function getWalletLabel(keypair: Keypair) {
        const base58Key = keypair.publicKey.toBase58();
        return `${base58Key.substring(0, 5)}...${base58Key.substring(base58Key.length - 5)}`;
    }
    const selectedWalletLabels = allWallets.map(getWalletLabel);

    const roleCounters: { [key: string]: number } = { MIX: 1, BUNDLEBUY: 1 };
    function getWalletRole(role: string) {
        const formattedRole = `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}`;

        if (roleCounters.hasOwnProperty(role)) {
            return `${formattedRole}${roleCounters[role]++}`;
        } else {
            return formattedRole;
        }
    }
    const selectedWalletRoles = allWalletRoles.map(getWalletRole);

    async function solCollecting(connection: Connection, wallets: Keypair[], receiver: Keypair) {
        try {
            logMessage('Starting SOL collection...');
            wallets = removeKeypairDuplicates(wallets);

            const MIN_RETRY_BALANCE = 0.0001;
            const MAX_RETRIES = 5;
            let retryCount = 0;

            while (wallets.length > 0 && retryCount < MAX_RETRIES) {
                const publicKeys = wallets.map((wallet) => wallet.publicKey);
                const balances = await getMultipleSolBalances(connection, publicKeys);

                const activeWallets: Keypair[] = [];
                const activeBalances: number[] = [];
                try {
                    for (let i = 0; i < balances.length; i++) {
                        if (wallets[i].publicKey.equals(receiver.publicKey)) {
                            continue;
                        }
                        if (balances[i] > MIN_RETRY_BALANCE) {
                            activeWallets.push(wallets[i]);
                            activeBalances.push(balances[i]);
                        }
                    }

                    if (activeWallets.length === 0) {
                        logMessage('Sol collecting finished.');
                        break;
                    }

                    const receivers = new Array(activeWallets.length).fill(receiver.publicKey);
                    console.log('sender', activeWallets[0].publicKey.toBase58());

                    const signaturesToConfirm = await multipleSolanaTrasfersWithJito(
                        connection,
                        receiver,
                        activeWallets,
                        receivers,
                        activeBalances,
                        solManagerJitoTip,
                        logMessage,
                    );

                    await Promise.all(
                        signaturesToConfirm.map((tx) =>
                            connection.confirmTransaction(
                                {
                                    blockhash: tx.blockhash,
                                    lastValidBlockHeight: tx.lastValidBlockHeight,
                                    signature: tx.signature,
                                },
                                'confirmed',
                            ),
                        ),
                    );
                } catch (txErr) {
                    logMessage(`Error during transfer attempt: ${txErr}`);
                }

                // Retry with remaining wallets
                wallets = activeWallets;
                retryCount++;
                logMessage(`Attempt ${retryCount} finished: Checking balances...`);
            }

            if (retryCount >= MAX_RETRIES) {
                logMessage('Reached maximum retry limit. Some wallets may still have funds.', 'error');
            } else {
                logMessage('All wallets are empty. Finishing...', 'success');
            }
        } catch (e) {
            logMessage(`Fatal error collecting: ${e}`);
        }
    }

    async function solDisperse(connection: Connection, walletFrom: Keypair, receivers: PublicKey[], values: number[]) {
        if (receivers.length === 0) {
            logMessage('No wallets to disperse to.');
            return;
        }

        const MAX_RETRIES = 5;
        let retryCount = 0;

        let pendingReceivers = [...receivers];
        let pendingValues = [...values];

        logMessage('Starting SOL disperse...');

        while (pendingReceivers.length > 0 && retryCount < MAX_RETRIES) {
            const fromBalance = await connection.getBalance(walletFrom.publicKey);
            const totalRequired = pendingValues.reduce((a, b) => a + b, 0);

            if (fromBalance < totalRequired) {
                logMessage(`Insufficient balance. Needed: ${totalRequired}, Available: ${fromBalance}`);
                return;
            }

            try {
                const signaturesToConfirm = await multipleSolanaTrasfersWithJito(
                    connection,
                    walletFrom,
                    new Array(pendingReceivers.length).fill(walletFrom),
                    pendingReceivers,
                    pendingValues,
                    solManagerJitoTip,
                    logMessage,
                );

                await Promise.all(
                    signaturesToConfirm.map((tx) =>
                        connection.confirmTransaction(
                            {
                                blockhash: tx.blockhash,
                                lastValidBlockHeight: tx.lastValidBlockHeight,
                                signature: tx.signature,
                            },
                            'confirmed',
                        ),
                    ),
                );
            } catch (e) {
                logMessage(`Error during transfer attempt ${retryCount + 1}: ${e}`);
            }

            const currentBalances = await getMultipleSolBalances(connection, pendingReceivers);

            const stillPendingReceivers: PublicKey[] = [];
            const stillPendingValues: number[] = [];

            for (let i = 0; i < pendingReceivers.length; i++) {
                if (currentBalances[i] < pendingValues[i]) {
                    stillPendingReceivers.push(pendingReceivers[i]);
                    stillPendingValues.push(pendingValues[i]);
                }
            }

            if (stillPendingReceivers.length === 0) {
                logMessage('All wallets successfully funded.', 'success');
                return;
            }

            logMessage(`${stillPendingReceivers.length} wallets still underfunded. Retrying...`);

            pendingReceivers = stillPendingReceivers;
            pendingValues = stillPendingValues;
            retryCount++;
        }

        if (retryCount >= MAX_RETRIES) {
            logMessage(`Disperse failed after ${MAX_RETRIES} retries. Some wallets may still be unfunded.`, 'error');
        }
    }

    async function customSolCollecting(file: File, connection: Connection, walletTo: Keypair) {
        logMessage('Custom sol collecting...');
        const parsedLines = await parseCsv(file);
        try {
            const wallets = parsedLines.map((line) => Keypair.fromSecretKey(bs58.decode(line[0])));

            //should get only 100 balances per request
            const values: number[] = [];
            for (let i = 0; i < wallets.length; i += 100) {
                values.push(
                    ...(await getMultipleSolBalances(
                        connection,
                        wallets.slice(i, i + 100).map((wallet) => wallet.publicKey),
                    )),
                );
            }

            const ids = await multipleSolanaTrasfersWithJito(
                connection,
                walletTo,
                wallets,
                new Array(wallets.length).fill(walletTo.publicKey),
                values,
                solManagerJitoTip,
            );

            logMessage(`All bundles sent and confirmed.`, 'success');
        } catch (e) {
            logMessage(`Error: ${e}`);
        }
    }

    async function customSolDispersing(connection: Connection, file: File, walletFrom: Keypair) {
        logMessage('Custom sol dispersing...');
        const parsedLines = await parseCsv(file);
        let addedValue = 0;
        let amountsIndex = 1;
        if (parsedLines[0].length > 2) {
            logMessage(`Non standard CSV format detected. Addint 0.02 Sol to each wallet to cover fees.`);
            addedValue = 0.02 * LAMPORTS_PER_SOL;
            amountsIndex = 4;
        }
        try {
            const publicKeys = parsedLines.map((line) => {
                try {
                    return Keypair.fromSecretKey(bs58.decode(line[0])).publicKey;
                } catch (e) {
                    return new PublicKey(line[0]);
                }
            });
            console.log(publicKeys);
            const values = parsedLines.map((line) => Math.floor(parseFloat(line[amountsIndex]) * LAMPORTS_PER_SOL + addedValue));
            console.log(values);
            const ids = await multipleSolanaTrasfersWithJito(
                connection,
                walletFrom,
                new Array(publicKeys.length).fill(walletFrom),
                publicKeys,
                values,
                solManagerJitoTip,
            );

            logMessage(`All bundles sent and confirmed.`, 'success');
        } catch (e) {
            logMessage(`${e}`);
        }
    }

    async function sendSol(connection: Connection, wallet: Keypair, receiver: PublicKey, amount: number, jitoTipLamport: number, useJito = false) {
        if (!walletLoaded) {
            logMessage('Wallet is not loaded');
            return;
        }

        if (amount <= 0) {
            logMessage('SOL amount is not valid');
            return;
        }

        try {
            logMessage('Sending SOL...');
            const signature = await transaferSolana(connection, wallet, wallet, receiver, amount, jitoTipLamport, useJito);
            logMessage(`SOL transfer sent. Signature: ${signature}`);
        } catch (error) {
            logMessage(`SOL transfer failed. Error: ${error}`);
        }
    }

    return (
        <section>
            <h4 className='token-manager-csv-title'>Solana - SOL using Loaded CSV</h4>
            <section className='token-option-container'>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Loaded Universal CSV - Disperse SOL</b>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #1</div>
                    </div>
                    <div className='token-option-description'>
                        <p>Source Wallet of SOL</p>
                        <select className='token-manager-csv-select' value={sourceWalletOfSol} onChange={sourceWalletOfSolChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                const allReceivers = [
                                    ...bundleBuyWallets.map((wallet) => wallet),
                                    ...[funder.publicKey, holder.publicKey, freezer.publicKey, volFunder?.publicKey!],
                                    ...volumeBuyWallets.map((wallet) => wallet.publicKey),
                                    ...mixWallets,
                                ];
                                const allValues = [
                                    ...bundleBuyValues.map((bundleBuyValue) => bundleBuyValue * LAMPORTS_PER_SOL + solManagerDisperseForBundleBuyFeesExtra),
                                    ...[0.25 * LAMPORTS_PER_SOL, 0.1 * LAMPORTS_PER_SOL, 0.25 * LAMPORTS_PER_SOL, 0.25 * LAMPORTS_PER_SOL],
                                    ...volumeBuyAmounts.map((amount) => amount * LAMPORTS_PER_SOL + solManagerVolumeBuyExtra),
                                    ...new Array(mixWallets.length).fill(solManagerDisperseForMixWalletFees),
                                ];

                                solDisperse(connection, allWallets[sourceWalletOfSol], allReceivers, allValues);
                            }}
                        >
                            Disperse All
                        </button>
                        <button
                            onClick={() =>
                                solDisperse(
                                    connection,
                                    allWallets[sourceWalletOfSol],
                                    mixWallets,
                                    new Array(mixWallets.length).fill(solManagerDisperseForMixWalletFees),
                                )
                            }
                        >
                            Disperse For Mix Wallet Fees
                        </button>
                        <button
                            onClick={() =>
                                solDisperse(
                                    connection,
                                    allWallets[sourceWalletOfSol],
                                    bundleBuyWallets,
                                    bundleBuyValues.map((bundleBuyValue) => bundleBuyValue * LAMPORTS_PER_SOL + solManagerDisperseForBundleBuyFeesExtra),
                                )
                            }
                        >
                            Disperse to Cover BundleBuys
                        </button>

                        <button
                            onClick={() =>
                                solDisperse(
                                    connection,
                                    allWallets[sourceWalletOfSol],
                                    [funder.publicKey, holder.publicKey, freezer.publicKey, volFunder?.publicKey!],
                                    [0.25 * LAMPORTS_PER_SOL, 0.1 * LAMPORTS_PER_SOL, 0.25 * LAMPORTS_PER_SOL, 0.25 * LAMPORTS_PER_SOL],
                                )
                            }
                        >
                            Disperse for Other Fees
                        </button>
                        <button
                            onClick={() =>
                                solDisperse(
                                    connection,
                                    allWallets[sourceWalletOfSol],
                                    volumeBuyWallets.map((wallet) => wallet.publicKey),
                                    volumeBuyAmounts.map((amount) => amount * LAMPORTS_PER_SOL + solManagerVolumeBuyExtra),
                                )
                            }
                        >
                            Disperse for Volume Buys
                        </button>
                        <button
                            onClick={async () => {
                                const balance = await connection.getBalance(allWallets[sourceWalletOfSol].publicKey, 'processed');
                                if (balance < LAMPORTS_PER_SOL) {
                                    logMessage('Source wallet does not have enough SOL to disperse.', 'error');
                                    return;
                                }
                                solDisperse(
                                    connection,
                                    allWallets[sourceWalletOfSol],
                                    volumeBuyWallets.map((wallet) => wallet.publicKey),
                                    getRandomizedArray(balance - LAMPORTS_PER_SOL, volumeBuyWallets.length, 50).map((amount) => Math.floor(amount)),
                                );
                            }}
                        >
                            VolTopup
                        </button>
                    </div>
                </div>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Loaded Universal CSV - Collect SOL</b>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #2</div>
                    </div>
                    <div className='token-option-description'>
                        <p>Target Wallet to Receive All SOL</p>
                        <select className='token-manager-csv-select' value={walletToReceiveAllSol} onChange={walletToReceiveAllSolChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                solCollecting(connection, [...allWallets, ...volumeBuyWallets], allWallets[walletToReceiveAllSol]);
                            }}
                        >
                            Collect All SOL
                        </button>
                        <button
                            onClick={() => {
                                solCollecting(connection, allWallets, allWallets[walletToReceiveAllSol]);
                            }}
                        >
                            Collect All SOL Ex. Volume
                        </button>

                        <button
                            onClick={() => {
                                solCollecting(connection, volumeBuyWallets, allWallets[walletToReceiveAllSol]);
                            }}
                        >
                            Collect SOL Only Volume
                        </button>
                    </div>
                </div>
            </section>
            <h4 className='token-manager-csv-title'>Solana - SOL using Custom CSV</h4>
            <section className='token-option-container'>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Custom CSV - Disperse SOL</b>
                        <button className='ex-file-button' onClick={downloadCustomSolDisperceExample}>
                            Ex. File
                        </button>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #3</div>
                    </div>
                    <div className='token-option-csv'>
                        <label className='token-option-csv-label' htmlFor='disperseSolFileInput'>
                            <span className='token-option-csv-svg'></span>
                            <span className='csv-file-name disperse-sol-csv-file-name'>Upload CSV with wallet addresses to disperse to</span>
                        </label>
                        <input type='file' accept='.csv' id='disperseSolFileInput' onChange={disperseSolCsv} />
                    </div>
                    <div className='token-option-description'>
                        <p>Source Wallet of SOL</p>
                        <select className='token-manager-csv-select' value={customSourceWalletOfSol} onChange={customSourceWalletOfSolChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                customSolDispersing(connection, disperseSolCsvFile!, allWallets[customSourceWalletOfSol]);
                            }}
                            disabled={!disperseSolCsvFile}
                        >
                            Disperse SOL
                        </button>
                    </div>
                </div>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Custom CSV - Collect SOL</b>
                        <button className='ex-file-button' onClick={downloadCustomSolCollectorExample}>
                            Ex. File
                        </button>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #4</div>
                    </div>
                    <div className='token-option-csv'>
                        <label className='token-option-csv-label' htmlFor='collectSolFileInput'>
                            <span className='token-option-csv-svg'></span>
                            <span className='csv-file-name collect-sol-csv-file-name'>Upload CSV with private keys to collect SOL</span>
                        </label>
                        <input type='file' accept='.csv' id='collectSolFileInput' onChange={collectSolCsv} />
                    </div>
                    <div className='token-option-description'>
                        <p>Target Wallet to Receive All SOL</p>
                        <select className='token-manager-csv-select' value={customWalletToReceiveAllSol} onChange={customWalletToReceiveAllSolChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                customSolCollecting(collectSolCsvFile!, connection, allWallets[customWalletToReceiveAllSol]);
                            }}
                            disabled={!collectSolCsvFile}
                        >
                            Collect All SOL
                        </button>
                    </div>
                </div>
            </section>
            <div className='csv-input-wrap'>
                <section className='receiver-jito-sol'>
                    <div className='sol-splitter-public-key'>
                        <p>Receiver Public Key</p>
                        <input type='text' value={splitterPublikKey} onChange={splitterPublikKeyChange} />
                    </div>

                    <div className='sol-amount'>
                        <p>SOL amount</p>
                        <input type='number' value={splitterSolAmount} onChange={splitterSolAmountChange} />
                    </div>

                    <div className='use-jito-checkbox'>
                        <label>
                            <input type='checkbox' checked={useJitoManualTransfer} onChange={(e) => setUseJitoManualTransfer(e.target.checked)} />
                            Use JITO
                        </label>
                    </div>

                    <div className='sol-splitter-public-key-settings'>
                        <p>JITO tip SOL</p>
                        <input
                            type='number'
                            value={splitterJitoTip}
                            onChange={(e) => setSplitterJitoTip(Number(e.target.value))}
                            disabled={!useJitoManualTransfer}
                        />
                    </div>

                    <button
                        className='button-accent button-200'
                        onClick={() =>
                            sendSol(
                                connection,
                                deployer,
                                new PublicKey(splitterPublikKey),
                                splitterSolAmount * LAMPORTS_PER_SOL,
                                splitterJitoTip * LAMPORTS_PER_SOL,
                                useJitoManualTransfer,
                            )
                        }
                        disabled={!walletLoaded}
                    >
                        Send
                    </button>
                </section>
            </div>

            <section className='wsol-unwrapper-container'>
                <div>
                    <h4 className='token-manager-csv-title'>WSOL UNWRAPPER</h4>
                    <section>
                        <button
                            onClick={async () => {
                                logMessage('Burning all WSOL token accounts with JITO...');
                                await burnAllTokenAccountsWithJito(
                                    connection,
                                    deployer.publicKey,
                                    removeKeypairDuplicates([...allWallets, ...volumeBuyWallets]),
                                    solManagerJitoTip,
                                    deployer,
                                    logMessage,
                                    new PublicKey(WSOL.mint),
                                );
                                logMessage('All WSOL token accounts burned with JITO.', 'success');
                            }}
                        >
                            UNWRAP SOL ALL WALLETS
                        </button>
                    </section>
                    <section>
                        <button onClick={() => wrapSol(connection, deployer, LAMPORTS_PER_SOL, logMessage)}>WRAP 1 SOL TO DEPLOYER</button>
                    </section>
                </div>
            </section>
        </section>
    );
}
